/**
 * Helpers de validation partagés client + serveur.
 *
 * Chaque helper renvoie un booléen. Pas de throw — on laisse Zod composer
 * le message d'erreur via `.refine()` ou `.superRefine()`.
 *
 * Sources :
 * - INSEE — méthode SIRENE pour le calcul Luhn et exceptions La Poste
 *   (https://www.insee.fr/fr/information/1972057)
 * - DGFiP / VIES — algo clé TVA intracommunautaire FR
 */

/**
 * Algorithme de Luhn (clé de contrôle modulo 10).
 *
 * Doublé une chiffre sur deux en partant de la DROITE (donc l'avant-dernière,
 * antépénultième…), si le doublage > 9 on retire 9. La somme totale doit
 * être divisible par 10.
 *
 * @param digits chaîne de chiffres uniquement (validation format à faire en amont)
 */
export function isValidLuhn(digits: string): boolean {
  let sum = 0;
  let shouldDouble = false;
  // On parcourt de droite à gauche : c'est la convention Luhn.
  for (let i = digits.length - 1; i >= 0; i--) {
    const code = digits.charCodeAt(i);
    // Garde-fou : si un caractère non-numérique a glissé, refuser net.
    if (code < 48 || code > 57) return false;
    let value = code - 48;
    if (shouldDouble) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

/**
 * Validation SIREN — 9 chiffres + Luhn.
 *
 * Exception INSEE : **La Poste (`356000000`)** ne respecte pas Luhn ; la règle
 * officielle est "somme des chiffres divisible par 5". On l'accepte.
 */
export function isValidSiren(siren: string): boolean {
  if (!/^\d{9}$/.test(siren)) return false;
  if (siren === "356000000") return sumDigits(siren) % 5 === 0;
  return isValidLuhn(siren);
}

/**
 * Validation SIRET — 14 chiffres + Luhn sur 14, ou règle La Poste.
 * Tout SIRET dont les 9 premiers chiffres correspondent à La Poste (`356000000`)
 * suit la règle "somme divisible par 5", pas Luhn.
 */
export function isValidSiret(siret: string): boolean {
  if (!/^\d{14}$/.test(siret)) return false;
  if (siret.startsWith("356000000")) return sumDigits(siret) % 5 === 0;
  return isValidLuhn(siret);
}

/**
 * N° TVA intracommunautaire France : `FR` + 2 caractères clé (chiffres ou
 * lettres sauf I/O/Q/U) + 9 chiffres SIREN.
 *
 * On valide le format ET le SIREN intégré. La cohérence de la clé numérique
 * (formule `(12 + 3*(SIREN%97))%97`) n'est pas vérifiée ici : la clé peut
 * être alphanumérique pour certains cas, et l'utilisateur a son n° de TVA
 * imprimé sur ses documents officiels — on ne le recalcule pas pour lui.
 */
export function isValidTvaIntraFr(tva: string): boolean {
  if (!/^FR[0-9A-HJ-NP-Z]{2}\d{9}$/.test(tva)) return false;
  const siren = tva.slice(4);
  return isValidSiren(siren);
}

/**
 * N° accise (entrepositaire agréé) — `FR0` + 5 chiffres bureau + lettre
 * statut (E ou W) + 4 chiffres ordre. **13 caractères** au total.
 * Source : douane.gouv.fr (CIEL/EMCS-Gamma) + alignement avec le CHECK
 * SQL `domaines_numero_accise_format_check` (migration 0005).
 *
 * Pour Champagne (vin), le statut est essentiellement `E` (entrepositaire
 * agréé), parfois `W` (entrepôt fiscal). N et R sont des statuts qui ne
 * concernent pas la filière vin.
 */
export function isValidNumeroAccise(numero: string): boolean {
  return /^FR0\d{5}[EW]\d{4}$/.test(numero);
}

/**
 * Code NAF/APE (nomenclature INSEE rév. 2). Format normalisé sans point :
 * 2 chiffres + 2 chiffres + 1 lettre. La saisie peut contenir un point
 * (ex : `01.21Z`) — appeler `normalizeCodeNaf()` avant validation.
 */
export function isValidCodeNaf(code: string): boolean {
  return /^\d{4}[A-Z]$/.test(code);
}

export function normalizeCodeNaf(input: string): string {
  return input.replace(/\./g, "").toUpperCase();
}

/** Code postal France (5 chiffres). DOM/Monaco/Corse couverts par le format. */
export function isValidCodePostalFr(cp: string): boolean {
  return /^\d{5}$/.test(cp);
}

/**
 * Code commune INSEE (Code Officiel Géographique). 5 caractères ;
 * Corse-du-Sud / Haute-Corse acceptent `2A`/`2B` en positions 1-2.
 */
export function isValidCommuneInsee(code: string): boolean {
  return /^(?:\d{5}|2[AB]\d{3})$/.test(code);
}

/**
 * Calcule le n° de TVA intracommunautaire FR à partir d'un SIREN valide.
 *
 * Algo officiel DGFiP : `clé = (12 + 3 × (SIREN mod 97)) mod 97`, formatée
 * sur 2 chiffres. Le résultat = `FR<clé><SIREN>` (13 caractères).
 *
 * Note : il existe une variante alphanumérique pour certains assujettis ;
 * on ne la calcule pas. L'utilisateur peut toujours saisir manuellement
 * la valeur si la clé numérique ne correspond pas à la sienne.
 *
 * @returns le n° TVA calculé, ou null si le SIREN n'est pas valide.
 */
export function computeTvaIntraFr(siren: string): string | null {
  const cleaned = siren.replace(/\s/g, "");
  if (!isValidSiren(cleaned)) return null;
  // parseInt sur 9 chiffres : pas de risque d'overflow (max 999_999_999 < 2^31).
  const sirenNum = parseInt(cleaned, 10);
  const cle = (12 + 3 * (sirenNum % 97)) % 97;
  return `FR${cle.toString().padStart(2, "0")}${cleaned}`;
}

/**
 * Déduit le département à partir d'un code commune INSEE.
 *
 * Règles :
 *   - Corse : `2A...` ou `2B...` → département `2A` ou `2B`.
 *   - DOM : `971..` → `971`, `972..` → `972`, etc. (3 chiffres).
 *   - Métropole standard : 2 premiers chiffres.
 *
 * Retourne `null` si le code INSEE est vide ou invalide.
 */
export function deriveDepartementFromInsee(codeInsee: string | null | undefined): string | null {
  if (!codeInsee || !isValidCommuneInsee(codeInsee)) return null;
  if (codeInsee.startsWith("2A") || codeInsee.startsWith("2B")) return codeInsee.slice(0, 2);
  if (codeInsee.startsWith("97")) return codeInsee.slice(0, 3);
  return codeInsee.slice(0, 2);
}

/** Helper interne : somme des chiffres décimaux d'une chaîne. */
function sumDigits(str: string): number {
  let sum = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 48 || code > 57) return 0;
    sum += code - 48;
  }
  return sum;
}
